import React, { FC, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import FetchTable from 'components/table/FetchTable';
import { buildClientEntity } from 'utils/helpers/client';
import { useActiveClientContext } from 'store/active-client';
import { Column, CellProps } from 'react-table';
import { ISelectOptionProps } from 'components/form/types';
import { buildFilters } from 'components/table/utils/filtersConfig';
import styled from 'styled-components';
import Chart from './chart';
import InnerWrapper from '../InnerWrapper';

interface ILeave extends Record<string, unknown> {
  month: string;
  leaves: ILeaveDetails;
}

interface ILeaveDetails {
  month: string;
  paid_ordinary: number;
  paid: number;
  unpaid: number;
  sick: number;
}

interface IResponseData {
  items: ILeave[];
}

const Statistics: FC = () => {
  const { t } = useTranslation();
  const { client } = useActiveClientContext();

  const [chartData, setChartData] = useState<ILeaveDetails[]>([]);
  const [filterYear, setFilterYear] = useState<number>(new Date().getFullYear());

  const generateYearOptions = (startYear: number, endYear: number): ISelectOptionProps[] => {
    const years: ISelectOptionProps[] = [];

    for (let year = startYear; year <= endYear; year += 1) {
      years.push({
        label: `${year.toString()} ${t('leaves.yearShort')}`,
        value: year.toString(),
        selected: year === filterYear,
      });
    }

    return years;
  };

  const monthNameToNumber = (monthName: string) => {
    const date = new Date(`${monthName} 1, 2000`);
    return date.getMonth() + 1;
  };

  const transformData = (data: ILeave): ILeaveDetails => {
    const monthNumber = monthNameToNumber(data.month);
    const monthName = t(`leaves.months.${monthNumber}`);
    return {
      month: monthName,
      paid: data.leaves.paid,
      paid_ordinary: data.leaves.paid_ordinary,
      sick: data.leaves.sick,
      unpaid: data.leaves.unpaid,
    };
  };

  const handleDataFetched = (data: IResponseData) => {
    const transformedData = data.items.map(transformData);
    setChartData(transformedData);
  };

  const columnMonths: Array<Column<ILeave>> = [
    {
      Header: t('leaves.month') || '',
      accessor: 'month',
      Cell: ({ value }: CellProps<ILeave, any>) => {
        const monthNumber = monthNameToNumber(value);
        return <>{t(`leaves.months.${monthNumber}`)}</>;
      },
    },
    {
      Header: t('leaves.paid') || '',
      accessor: 'leaves.paid_ordinary',
    },
    {
      Header: t('leaves.otherPaid') || '',
      accessor: 'leaves.paid',
    },
    {
      Header: t('leaves.unpaid') || '',
      accessor: 'leaves.unpaid',
    },
    {
      Header: t('leaves.sick') || '',
      accessor: 'leaves.sick',
    },
  ];

  const columnGroups: Array<Column<ILeave>> = [
    {
      Header: t('leaves.group') || '',
      accessor: 'group',
    },
    {
      Header: t('leaves.userCount') || '',
      accessor: 'users_count',
    },
    {
      Header: t('leaves.paid') || '',
      accessor: 'leaves.paid_ordinary',
    },
    {
      Header: t('leaves.otherPaid') || '',
      accessor: 'leaves.paid',
    },
    {
      Header: t('leaves.unpaid') || '',
      accessor: 'leaves.unpaid',
    },
    {
      Header: t('leaves.sick') || '',
      accessor: 'leaves.sick',
    },
  ];

  const tableFiltersMonth = useMemo(
    () => [
      buildFilters({
        name: 'type',
        placeholder: t('table.typeFilter'),
        options: [
          {
            value: 'all',
            label: 'all',
            selected: true,
          },
        ],
        hidden: true,
      }),
      buildFilters({
        name: 'year',
        placeholder: t('leaves.year'),
        options: generateYearOptions(2020, new Date().getFullYear() + 10),
      }),
    ],
    [filterYear]
  );

  const tableFiltersGroup = useMemo(
    () => [
      buildFilters({
        name: 'type',
        placeholder: t('table.typeFilter'),
        options: [
          {
            value: 'group',
            label: 'group',
            selected: true,
          },
        ],
        hidden: true,
      }),
      buildFilters({
        name: 'leave_period_start',
        placeholder: t('leaves.dateFrom'),
        type: 'monthyearpickerstart',
      }),
      buildFilters({
        name: 'leave_period_end',
        placeholder: t('leaves.dateTo'),
        type: 'monthyearpickerend',
        subType: 'leavesWaitingApproval',
      }),
    ],
    [t]
  );

  return (
    <InnerWrapper>
      <ChartAndTableContainer>
        <Chart chartData={chartData} />

        <p className="text-center mt-3">
          <i>{t('leaves.monthTableWarning')}</i>
        </p>

        <FetchTable<ILeave>
          entityEndpoint="leaves/statistic"
          columns={columnMonths}
          entityName={buildClientEntity('leaves', client)}
          filters={tableFiltersMonth}
          hideSearch
          onDataFetched={handleDataFetched}
        />
      </ChartAndTableContainer>

      <hr />

      <p className="text-center mt-5">
        <i>{t('leaves.groupTableWarning')}</i>
      </p>

      <FetchTable<ILeave>
        entityEndpoint="leaves/statistic"
        columns={columnGroups}
        entityName={buildClientEntity('leaves', client)}
        filters={tableFiltersGroup}
        hideSearch
        pagination
        pageLimit={10}
      />
    </InnerWrapper>
  );
};

export default Statistics;

const ChartAndTableContainer = styled.div`
  position: relative;
  margin-top: 40px;

  .filterBar {
    position: absolute;
    top: 0px;
    right: 0px;
    z-index: 10;

    @media (max-width: 991px) {
      top: -40px;
    }
  }
`;
